import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Modal,
  TextField,
  Typography,
  Paper,
  Pagination,
  useMediaQuery,
  Tooltip,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
} from "@mui/material";

import * as XLSX from "xlsx";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
} from "firebase/firestore";

import AddIcon from "@mui/icons-material/Add";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import CloseIcon from "@mui/icons-material/Close";
import { Edit, Delete } from "@mui/icons-material";

/**
 * CONTACTS component with divisionId (stores only division doc ID)
 * - Division collection: bdivisions
 * - Division document shape: { division: "Pune", created_at: Timestamp }
 * - Contacts store: { ...otherFields, divisionId: "<docId>" }
 */

export default function Contacts() {
  const [list, setList] = useState([]); // contacts
  const [filteredList, setFilteredList] = useState([]);

  const [divisions, setDivisions] = useState([]); // division docs array
  const [divisionMap, setDivisionMap] = useState({}); // id => division name
  const [divisionNameToId, setDivisionNameToId] = useState({}); // lowerName => id

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const [form, setForm] = useState({
    parentName: "",
    mobile: "",
    whatsapp: "",
    email: "",
    address: "",
    status: "Active",
    divisionId: "", // store only id
  });

  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");

  // pagination
  const [page, setPage] = useState(1);
  const perPage = 20;

  const isMobile = useMediaQuery("(max-width: 768px)");

  // -----------------------------
  // FETCH DIVISIONS & BUILD MAPS
  // -----------------------------
  const fetchDivisions = async () => {
    try {
      const q = query(collection(db, "bdivisions"), orderBy("division"));
      const snap = await getDocs(q);
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDivisions(arr);

      const idToName = {};
      const nameToId = {};
      for (const d of arr) {
        const name = (d.division || "").toString();
        idToName[d.id] = name;
        nameToId[name.toLowerCase()] = d.id;
      }
      setDivisionMap(idToName);
      setDivisionNameToId(nameToId);
    } catch (err) {
      console.error("fetchDivisions:", err);
      alert("Failed to fetch divisions");
    }
  };

  // -----------------------------
  // FETCH CONTACT LIST
  // -----------------------------
  const fetchContacts = async () => {
    try {
      const q = query(collection(db, "contacts"), orderBy("created_at", "desc"));
      const querySnapshot = await getDocs(q);

      const arr = querySnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      setList(arr);
      setFilteredList(arr);
    } catch (err) {
      console.error("fetchContacts:", err);
      alert("Failed to fetch contacts");
    }
  };

  useEffect(() => {
    // fetch divisions first (so we can resolve names), then contacts
    (async () => {
      await fetchDivisions();
      await fetchContacts();
    })();
  }, []);

  // -----------------------------
  // HELPER: Ensure division exists and return divisionId
  // If not present, create new division doc (with field `division`) and return id.
  // -----------------------------
  const ensureDivisionIdByName = async (divisionNameRaw) => {
    const name = (divisionNameRaw || "").toString().trim();
    if (!name) return "";

    const lower = name.toLowerCase();
    // if exists in current map, return id
    if (divisionNameToId[lower]) return divisionNameToId[lower];

    // otherwise create new division
    try {
      const ref = await addDoc(collection(db, "bdivisions"), {
        division: name,
        created_at: new Date(),
      });
      // refresh divisions to update maps
      await fetchDivisions();
      return ref.id;
    } catch (err) {
      console.error("ensureDivisionIdByName:", err);
      return "";
    }
  };

  // -----------------------------
  // HANDLE SEARCH
  // -----------------------------
  useEffect(() => {
    const s = (search || "").toLowerCase();
    const f = list.filter((x) => {
      const mobile = (x.mobile || "").toString().toLowerCase();
      const name = (x.parentName || "").toString().toLowerCase();
      const divisionName = (divisionMap[x.divisionId] || "").toString().toLowerCase();
      return (
        mobile.includes(s) ||
        name.includes(s) ||
        divisionName.includes(s)
      );
    });

    setFilteredList(f);
    setPage(1);
    if (s && showAll) setShowAll(false);
  }, [search, list, divisionMap, showAll]);

  // -----------------------------
  // OPEN ADD / EDIT MODAL
  // -----------------------------
  const handleOpen = (item = null) => {
    setEditing(item);
    if (item) {
      setForm({
        parentName: item.parentName || "",
        mobile: item.mobile || "",
        whatsapp: item.whatsapp || "",
        email: item.email || "",
        address: item.address || "",
        status: item.status || "Active",
        divisionId: item.divisionId || "",
      });
    } else {
      setForm({
        parentName: "",
        mobile: "",
        whatsapp: "",
        email: "",
        address: "",
        status: "Active",
        divisionId: "",
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  // -----------------------------
  // SAVE NEW / UPDATE CONTACT
  // -----------------------------
  const handleSave = async () => {
    if (!form.mobile) {
      alert("Mobile number required");
      return;
    }
    console.log(form);

    try {
      let divisionIdToSave = form.divisionId || "";
      if (divisionIdToSave && !divisionMap[divisionIdToSave]) {
        divisionIdToSave = await ensureDivisionIdByName(divisionIdToSave);
      }

      if (editing) {
        await updateDoc(doc(db, "contacts", editing.id), {
          ...form,
          divisionId: divisionIdToSave,
        });
      } else {
        await addDoc(collection(db, "contacts"), {
          ...form,
          divisionId: divisionIdToSave || "",
          created_at: new Date() || "",
        });
      }

      await fetchContacts();
      handleClose();
    } catch (err) {
      console.error("handleSave:", err);
      alert("Error saving contact");
    }
  };

  // -----------------------------
  // DELETE CONTACT
  // -----------------------------
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await deleteDoc(doc(db, "contacts", id));
      await fetchContacts();
    } catch (err) {
      console.error("handleDelete:", err);
      alert("Delete failed");
    }
  };

  // -----------------------------
  // EXCEL IMPORT HANDLER (mobile + division column)
  // -----------------------------
  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);

    try {
      await fetchContacts();
      await fetchDivisions();

      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      // Convert list into lookup for faster existing mobile search
      const existingContactsMap = {};
      list.forEach((c) => {
        existingContactsMap[String(c.mobile)] = c;
      });

      let imported = 0;
      let updated = 0;

      for (const row of rows) {
        const mobile =
          String(
            row.mobile ||
            row.Mobile ||
            row.MOBILE ||
            row["Mobile Number"] ||
            ""
          ).trim();

        if (!mobile) continue;

        // Read division name
        const divisionName =
          row.division ||
          row.Division ||
          row.DIVISION ||
          row.city ||
          row.City ||
          "";

        let divisionId = "";
        if (divisionName) {
          divisionId = await ensureDivisionIdByName(divisionName);
        }

        const parentName =
          row.parentName ||
          row.ParentName ||
          row.name ||
          row.Name ||
          "";

        // If contact with same mobile exists → UPDATE ONLY division
        if (existingContactsMap[mobile]) {
          const contact = existingContactsMap[mobile];
          await updateDoc(doc(db, "contacts", contact.id), {
            divisionId: divisionId || "",
            updated_at: new Date(),
          });
          updated++;
          continue;
        }

        // ELSE → CREATE NEW CONTACT
        await addDoc(collection(db, "contacts"), {
          parentName: parentName || "",
          mobile,
          whatsapp: mobile,
          email: "",
          address: "",
          status: "Active",
          divisionId: divisionId || "",
          created_at: new Date(),
        });

        imported++;
      }

      await fetchContacts();

      alert(
        `Excel import completed:\n` +
        `${imported} new contacts added\n` +
        `${updated} existing contacts updated`
      );
    } catch (err) {
      console.error("Excel import error:", err);
      alert("Failed to import Excel");
    } finally {
      setImporting(false);
      e.target.value = null;
    }
  };


  const handleVCFUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const contacts = parseVCF(text);

      if (!contacts.length) {
        alert("No contacts found in VCF file");
        return;
      }

      const existingMobiles = new Set(list.map(item => String(item.mobile || "")));

      let imported = 0;

      for (const c of contacts) {
        if (!c.mobile) continue;
        if (existingMobiles.has(c.mobile)) continue;

        await addDoc(collection(db, "contacts"), {
          parentName: c.name || "",
          mobile: c.mobile,
          whatsapp: c.mobile,
          email: c.email || "",
          address: "",
          status: "Active",
          divisionId: "",
          created_at: new Date(),
        });

        existingMobiles.add(c.mobile);
        imported++;
      }

      await fetchContacts();
      alert(`Imported ${imported} contact(s) from VCF.`);
    } catch (err) {
      console.error("VCF Import Error:", err);
      alert("Failed to import VCF file");
    }
  };


  // -----------------------------
  // MOBILE CONTACTS (Web Contacts Picker API)
  // -----------------------------
  const handleMobileContacts = async () => {
    const supportsPicker =
      "contacts" in navigator && typeof navigator.contacts.select === "function";
    if (!supportsPicker) {
      alert(
        "Phone contact import is not supported on this device/browser. (Android Chrome required)"
      );
      return;
    }

    try {
      await fetchContacts();
      const props = ["name", "tel", "email"];
      const opts = { multiple: true };

      const contactsPicked = await navigator.contacts.select(props, opts);
      if (!contactsPicked || contactsPicked.length === 0) return;

      const existingMobiles = new Set(list.map((x) => String(x.mobile || "")));

      let imported = 0;
      for (const c of contactsPicked) {
        const name = Array.isArray(c.name) ? c.name[0] : c.name || "";
        const telRaw = Array.isArray(c.tel) ? c.tel[0] : c.tel || "";
        const mobile = String(telRaw || "")
          .replace(/[^\d+]/g, "")
          .trim();
        const email = Array.isArray(c.email) ? c.email[0] : c.email || "";

        if (!mobile) continue;
        if (existingMobiles.has(mobile)) continue;

        await addDoc(collection(db, "contacts"), {
          parentName: name || "",
          mobile,
          whatsapp: mobile,
          email: email || "",
          address: "",
          status: "Active",
          divisionId: "",
          created_at: new Date(),
        });

        existingMobiles.add(mobile);
        imported += 1;
      }

      await fetchContacts();
      alert(`Imported ${imported} contact(s) from phone.`);
    } catch (err) {
      console.error("handleMobileContacts:", err);
      alert(
        "Failed to import phone contacts (user may have cancelled or permission denied)."
      );
    }
  };

  // -----------------------------
  // PAGINATION (or showAll)
  // -----------------------------
  const paginated = showAll
    ? filteredList
    : filteredList.slice((page - 1) * perPage, page * perPage);

  // -----------------------------
  // MOBILE CARD VIEW
  // -----------------------------
  const renderMobileCard = (item, index) => {
    const sr = showAll ? index + 1 : (page - 1) * perPage + index + 1;
    return (
      <Box
        key={item.id}
        p={2}
        mb={2}
        sx={{
          background: "#fff",
          borderRadius: "10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography>
          <b>Sr:</b> {sr}
        </Typography>
        <Typography>
          <b>Name:</b> {item.parentName}
        </Typography>
        <Typography>
          <b>Mobile:</b> {item.mobile}
        </Typography>
        <Typography>
          <b>Division:</b> {divisionMap[item.divisionId] || "-"}
        </Typography>
        <Typography>
          <b>Email:</b> {item.email}
        </Typography>
        <Typography>
          <b>Address:</b> {item.address}
        </Typography>
        <Typography>
          <b>Status:</b> {item.status}
        </Typography>
        <Typography>
          <b>Created At:</b> {formatDate(item.created_at)}
        </Typography>

        <Box mt={1} display="flex" justifyContent="flex-end" gap={1}>
          <Button size="small" onClick={() => handleOpen(item)}>
            Edit
          </Button>
          <Button size="small" color="error" onClick={() => handleDelete(item.id)}>
            Delete
          </Button>
        </Box>
      </Box>
    );
  };


  const formatDate = (ts) => {
    if (!ts) return "-";
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  function parseVCF(text) {
    const records = text.split("BEGIN:VCARD").slice(1);
    const contacts = [];

    for (const rec of records) {
      let name = "";
      let mobile = "";
      let email = "";

      const lines = rec.split(/\r?\n/);

      for (let line of lines) {
        line = line.trim();

        // Name
        if (line.startsWith("FN:")) {
          name = line.replace("FN:", "").trim();
        }

        // Phone
        if (line.startsWith("TEL")) {
          const num = line.substring(line.indexOf(":") + 1).trim();
          mobile = num.replace(/[^\d+]/g, "");
        }

        // Email
        if (line.startsWith("EMAIL")) {
          email = line.substring(line.indexOf(":") + 1).trim();
        }
      }

      if (mobile) {
        contacts.push({ name, mobile, email });
      }
    }

    return contacts;
  }




  return (
    <Box p={isMobile ? 1 : 3}>
      {/* Header Section */}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "center"}
        gap={2}
        mb={2}
        sx={{
          p: isMobile ? 2 : 2,
          background: "#ffffff",
          borderRadius: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        {/* LEFT SIDE */}
        <Box flex={isMobile ? "unset" : 1}>
          <Typography
            variant="h6"
            sx={{
              fontSize: isMobile ? 18 : 20,
              fontWeight: 700,
              color: "#333",
              mb: 1,
            }}
          >
            Contacts ({list.length})
          </Typography>

          {/* ACTION BUTTONS LEFT */}
          <Box
            display="flex"
            flexWrap="wrap"
            gap={1}
            mt={1}
            sx={{
              "& button": { borderRadius: 2, textTransform: "none" },
            }}
          >
            {/* VCF Import */}
            <Button variant="outlined" size="small" component="label">
              Import VCF
              <input type="file" hidden accept=".vcf" onChange={handleVCFUpload} />
            </Button>

            {/* Show All / Exit */}
            {!showAll ? (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setShowAll(true);
                  setSearch("");
                  setFilteredList(list);
                  setPage(1);
                }}
              >
                Show All
              </Button>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloseIcon />}
                onClick={() => {
                  setShowAll(false);
                  setPage(1);
                }}
              >
                Exit
              </Button>
            )}

            {/* Mobile Contacts */}
            <Tooltip title="Import phone contacts (Android Chrome only)">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PhoneAndroidIcon />}
                  onClick={handleMobileContacts}
                >
                  Phone Contacts
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* SEARCH BAR */}
        <Box flex={isMobile ? "unset" : 1}>
          <TextField
            label="Search Contacts"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            sx={{
              background: "#f8f8f8",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
        </Box>

        {/* RIGHT BUTTONS */}
        <Box
          display="flex"
          gap={1}
          flex={isMobile ? "unset" : "none"}
          flexWrap="wrap"
          justifyContent={isMobile ? "space-between" : "flex-end"}
          sx={{ "& button": { borderRadius: 2, textTransform: "none" } }}
        >
          {/* Excel Import */}
          <Button
            variant="outlined"
            component="label"
            size="small"
            disabled={importing}
            sx={{ minWidth: isMobile ? "48%" : "auto" }}
          >
            {importing ? "Importing..." : "Import Excel"}
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={handleExcelUpload}
            />
          </Button>

          {/* Add Contact */}
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpen}
            sx={{
              minWidth: isMobile ? "48%" : "auto",
              background: "#1976d2",
              "&:hover": { background: "#155a9c" },
            }}
          >
            Add
          </Button>
        </Box>
      </Box>




      {/* Mobile View */}
      {isMobile ? (
        paginated.map((item, i) => renderMobileCard(item, i))
      ) : (
        <Paper sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead
              sx={{
                background: "#f0f0f0",
                position: "sticky",
                top: 0,
                zIndex: 5,
              }}
            >
              <TableRow>
                <TableCell>Sr</TableCell>
                <TableCell>Parent Name</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Division</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {/* ONE ROW VIEW WHEN SHOW ALL = TRUE */}
              {showAll ? (
                <TableRow>
                  {filteredList.map((item, i) => (
                    <TableCell
                      key={item.id}
                      sx={{
                        minWidth: 250,
                        borderRight: "1px solid #ddd",
                        whiteSpace: "nowrap",
                        verticalAlign: "top",
                      }}
                    >
                      <Typography variant="body2">
                        <b>Sr:</b> {i + 1}
                      </Typography>
                      <Typography variant="body2">
                        <b>Name:</b> {item.parentName}
                      </Typography>
                      <Typography variant="body2">
                        <b>Mobile:</b> {item.mobile}
                      </Typography>
                      <Typography variant="body2">
                        <b>Division:</b> {divisionMap[item.divisionId] || "-"}
                      </Typography>
                      <Typography variant="body2">
                        <b>Email:</b> {item.email}
                      </Typography>
                      <Typography variant="body2">
                        <b>Status:</b> {item.status}
                      </Typography>
                      <Typography variant="body2">
                        <b>Created At:</b> {formatDate(item.created_at)}
                      </Typography>

                      <Box mt={1} display="flex" gap={1}>
                        <Button size="small" onClick={() => handleOpen(item)}>
                          Edit
                        </Button>
                        <Button size="small" color="error" onClick={() => handleDelete(item.id)}>
                          Delete
                        </Button>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              ) : (
                /* NORMAL PAGINATION VIEW */
                paginated.map((item, i) => (
                  <TableRow key={item.id}>
                    <TableCell>{(page - 1) * perPage + i + 1}</TableCell>
                    <TableCell>{item.parentName}</TableCell>
                    <TableCell>{item.mobile}</TableCell>
                    <TableCell>{divisionMap[item.divisionId] || "-"}</TableCell>
                    <TableCell>{item.email}</TableCell>
                    <TableCell>{item.address}</TableCell>
                    <TableCell>{item.status}</TableCell>

                    <TableCell>{formatDate(item.created_at)}</TableCell>

                    <TableCell align="right">
                      <IconButton color="primary" onClick={() => handleOpen(item)}>
                        <Edit />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(item.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Pagination */}
      {!showAll && (
        <Box mt={2} display="flex" justifyContent="center">
          <Pagination
            count={Math.max(1, Math.ceil(filteredList.length / perPage))}
            page={page}
            onChange={(e, v) => setPage(v)}
          />
        </Box>
      )}

      {/* Add/Edit Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            p: 3,
            bgcolor: "white",
            width: isMobile ? "90%" : 480,
            mx: "auto",
            mt: "12%",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" mb={2}>
            {editing ? "Edit Contact" : "Add Contact"}
          </Typography>

          <TextField
            label="Parent Name"
            fullWidth
            value={form.parentName}
            onChange={(e) => setForm({ ...form, parentName: e.target.value })}
            margin="dense"
          />

          <TextField
            label="Mobile"
            fullWidth
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
            margin="dense"
          />

          <TextField
            label="Whatsapp"
            fullWidth
            value={form.whatsapp}
            onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
            margin="dense"
          />

          <TextField
            label="Email"
            fullWidth
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            margin="dense"
          />

          <TextField
            label="Address"
            fullWidth
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            margin="dense"
          />

          <FormControl fullWidth margin="dense">
            <InputLabel id="division-select-label">Division (City)</InputLabel>
            <Select
              labelId="division-select-label"
              value={form.divisionId}
              label="Division (City)"
              onChange={(e) => setForm({ ...form, divisionId: e.target.value })}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {divisions.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.division}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
